import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { clientApi, publicApi } from '../lib/api';
import { ChefHat, Utensils, ShoppingCart, Calendar, Leaf, ArrowRight, Check, Mail, Phone, Percent, Star, Facebook, Instagram, Linkedin, Youtube, Twitter } from 'lucide-react';
import PageSeo from '../components/PageSeo';
import { PromoBanner } from '../components/PromoBanner';
import { toast } from 'sonner';
import { OFFICIAL_PHONE_DISPLAY, OFFICIAL_PHONE_TEL, trackPhoneClick } from '../lib/contact';

const DEFAULT_HERO_IMAGE = "https://images.pexels.com/photos/4590941/pexels-photo-4590941.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DEFAULT_VEGETABLES_IMAGE = "https://images.pexels.com/photos/5792479/pexels-photo-5792479.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DEFAULT_FAMILY_IMAGE = "https://images.unsplash.com/photo-1627662167802-d58480978ec0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxmYW1pbHklMjBlYXRpbmclMjBkaW5uZXIlMjBoYXBweXxlbnwwfHx8fDE3NzQ2MDE5MzZ8MA&ixlib=rb-4.1.0&q=85";

// Contenu éditorial de l'accueil : source unique = Configuration Center (backend).
// Aucun jeu de défauts dupliqué ici (un champ vidé volontairement reste vide).

export default function LandingPage() {
  const { user, login } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const submitMagic = async (e) => {
    e.preventDefault();
    if (!magicEmail.trim()) return;
    setMagicSending(true);
    try {
      await publicApi.requestMagicLink(magicEmail.trim());
      setMagicSent(true);
    } catch (err) {
      setMagicSent(true); // réponse neutre : on affiche le même message quoi qu'il arrive
    } finally {
      setMagicSending(false);
    }
  };
  const { config, getLogoHeight } = useConfig();
  const [pricing, setPricing] = useState({ formulas: [], options: [] });
  const [authError, setAuthError] = useState(null);
  const [featuredReview, setFeaturedReview] = useState(null);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [homeContent, setHomeContent] = useState(null);
  const [promoStatus, setPromoStatus] = useState(null);
  const promoAvailable = !!promoStatus?.available;
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', callback: false, message: '', website: '' });
  const [contactStatus, setContactStatus] = useState({ loading: false, success: false, error: '' });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const { name, email, phone, callback, message } = contactForm;
    if (!name.trim() || !email.trim() || !message.trim()) {
      setContactStatus({ loading: false, success: false, error: 'Merci de renseigner votre nom, votre e-mail et votre message.' });
      return;
    }
    if (callback && !phone.trim()) {
      setContactStatus({ loading: false, success: false, error: 'Le numéro de téléphone est obligatoire pour être rappelé(e).' });
      return;
    }
    setContactStatus({ loading: true, success: false, error: '' });
    try {
      await publicApi.submitContact(contactForm);
      setContactStatus({ loading: false, success: true, error: '' });
      setContactForm({ name: '', email: '', phone: '', callback: false, message: '', website: '' });
    } catch (err) {
      setContactStatus({ loading: false, success: false, error: err.response?.data?.detail || 'Une erreur est survenue. Merci de réessayer.' });
    }
  };

  useEffect(() => {
    loadPricing();
    loadReviewAndMenu();
    const fromCampaign = new URLSearchParams(window.location.search).has('offre');
    publicApi.getPromoStatus().then((r) => {
      setPromoStatus(r.data);
      // Le lien de campagne n'accorde rien : l'éligibilité reste vérifiée côté serveur
      if (fromCampaign && !r.data?.available) {
        toast.info("L'offre découverte n'est pas disponible actuellement. Nos tarifs habituels s'appliquent.", { id: 'promo-unavailable' });
      }
    }).catch(() => {});
    // Check for auth error message
    const error = sessionStorage.getItem('auth_error');
    if (error) {
      setAuthError(error);
      sessionStorage.removeItem('auth_error');
    }
    
    // Handle resize for logo
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadPricing = async () => {
    try {
      const pricingRes = await clientApi.getPricing();
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Error loading pricing:', error);
    }
  };

  const loadReviewAndMenu = async () => {
    try {
      const [rev, menu, home] = await Promise.all([
        publicApi.getFeaturedReview().catch(() => ({ data: null })),
        publicApi.getCurrentMenu().catch(() => ({ data: null })),
        publicApi.getPageContent('accueil').catch(() => ({ data: null })),
      ]);
      setFeaturedReview(rev?.data || null);
      setCurrentMenu(menu?.data && menu.data.has_menu ? menu.data : null);
      setHomeContent(home?.data || null);
    } catch (error) {
      // non bloquant
    }
  };

  // Use config values with fallbacks
  const logoUrl = config?.logo_url || 'https://customer-assets.emergentagent.com/job_cooking-planner/artifacts/9loa2439_logo.png';
  const logoHeight = getLogoHeight ? getLogoHeight(screenWidth) : 48;
  const heroImage = config?.hero_image_url || DEFAULT_HERO_IMAGE;
  const vegetablesImage = config?.vegetables_image_url || DEFAULT_VEGETABLES_IMAGE;
  const aboutImage = config?.about_image_url || DEFAULT_FAMILY_IMAGE;
  const slogan = config?.slogan || "Profitez de votre soirée, on s'occupe du dîner !";
  const heroSubtitle = config?.homepage_subtitle ?? '';
  const contactEmail = config?.email || 'contact@mitonner-essentiel.fr';
  const contactPhone = config?.phone || '01 23 45 67 89';
  const sapEnabled = config?.sap_enabled ?? true;
  const sapNumber = config?.sap_number || 'SAP102115565';
  const sapMarketingText = config?.sap_marketing_text || 'Bénéficiez de 50% de crédit d\'impôt sur nos prestations';
  const sapDeclarationText = config?.sap_declaration_text || 'Déclaration de services à la personne enregistrée sous le n°';
  const footerText = config?.footer_text ?? '';
  const socialMedia = config?.social_media || {};

  // Contenu éditorial administrable — source unique : Configuration Center (backend).
  // Un champ vidé volontairement reste vide (plus de repli sur un ancien texte).
  const aboutTitle = config?.about_title ?? '';
  const aboutText1 = config?.about_text1 ?? '';
  const aboutText2 = config?.about_text2 ?? '';
  const ctaTitle = config?.cta_title ?? '';
  const ctaSubtitle = config?.cta_subtitle ?? '';

  const content = config || {};
  const features = [
    { icon: <ChefHat className="w-8 h-8" />, title: content.feature1_title, description: content.feature1_desc },
    { icon: <Utensils className="w-8 h-8" />, title: content.feature2_title, description: content.feature2_desc },
    { icon: <ShoppingCart className="w-8 h-8" />, title: content.feature3_title, description: content.feature3_desc },
    { icon: <Calendar className="w-8 h-8" />, title: content.feature4_title, description: content.feature4_desc }
  ];

  const steps = [
    { number: "01", title: content.step1_title, description: content.step1_desc },
    { number: "02", title: content.step2_title, description: content.step2_desc },
    { number: "03", title: content.step3_title, description: content.step3_desc },
    { number: "04", title: content.step4_title, description: content.step4_desc },
    { number: "05", title: content.step5_title, description: content.step5_desc }
  ];

  // Tarifs : source unique = tarifs actifs de l'admin (mêmes données que le reste à charge).
  const formulas = pricing.formulas.map(f => ({
    name: f.name,
    persons: f.description,
    price: `${f.price}€`,
    note_html: f.note_html || '',
    popular: f.key === 'moyen'
  }));

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <PageSeo
        title={homeContent?.seo_title || 'Préparation de repas à domicile à Hazebrouck | Mitonner'}
        description={homeContent?.meta_description || 'Mitonner prépare 10 repas directement à votre domicile à Hazebrouck et alentours. Menus de saison, liste de courses et crédit d\u2019impôt de 50 %.'}
      />
      {/* Auth Error Banner */}
      {authError && (
        <div className="bg-[#C86B51] text-white py-3 px-4 text-center">
          <p className="text-sm">{authError}</p>
          <button 
            onClick={() => setAuthError(null)}
            className="ml-4 text-white/80 hover:text-white underline text-sm"
          >
            Fermer
          </button>
        </div>
      )}
      
      {promoAvailable && <PromoBanner isLoggedIn={!!user} onLogin={() => setAuthOpen(true)} remaining={promoStatus.remaining} />}

      {/* Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img 
                src={logoUrl} 
                alt={config?.company_name || 'Mitonner'} 
                style={{ height: `${logoHeight}px` }}
                className="object-contain"
              />
            </Link>
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/comment-ca-marche" className="text-[#5C6A61] hover:text-[#2A312C] transition-colors">Comment ça marche</Link>
              <Link to="/menus" className="text-[#5C6A61] hover:text-[#2A312C] transition-colors">Menus</Link>
              <Link to="/tarifs" className="text-[#5C6A61] hover:text-[#2A312C] transition-colors">Tarifs</Link>
              <Link to="/services-a-la-personne" className="text-[#5C6A61] hover:text-[#2A312C] transition-colors">SAP & Crédit d&apos;impôt</Link>
              <Link to="/contenants-conservation" className="text-[#5C6A61] hover:text-[#2A312C] transition-colors">Contenants & conservation</Link>
              <Link to="/zone-intervention" className="text-[#5C6A61] hover:text-[#2A312C] transition-colors">Zone</Link>
              <Link to="/avis" className="text-[#5C6A61] hover:text-[#2A312C] transition-colors" data-testid="nav-reviews-link">Avis clients</Link>
            </nav>
            <div className="flex items-center gap-3 md:flex-col-reverse md:gap-1">
              <a
                href={OFFICIAL_PHONE_TEL}
                onClick={() => trackPhoneClick('header')}
                title={`Une question ? Appelez-nous au ${OFFICIAL_PHONE_DISPLAY}`}
                className="inline-flex items-center gap-2 md:gap-1.5 text-sm md:text-xs font-medium text-[#2A312C] border md:border-0 border-[#E3DED5] rounded-full px-3 py-2 md:p-0 hover:border-[#A5BBA8] md:hover:text-[#C86B51] transition-colors whitespace-nowrap"
                aria-label={`Une question ? Appelez-nous au ${OFFICIAL_PHONE_DISPLAY}`}
                data-testid="header-phone-btn"
              >
                <Phone className="w-4 h-4 md:w-3.5 md:h-3.5 text-[#A5BBA8]" />
                <span className="md:hidden">Appelez-nous</span>
                <span className="hidden md:inline">{OFFICIAL_PHONE_DISPLAY}</span>
              </a>
              {user ? (
                <Link 
                  to={user.role === 'admin' ? '/admin' : user.role === 'employee' ? '/employee' : '/client'}
                  className="btn-primary"
                  data-testid="dashboard-btn"
                >
                  Mon espace
                </Link>
              ) : (
                <button 
                  onClick={() => setAuthOpen(true)}
                  className="btn-primary"
                  data-testid="login-btn"
                >
                  Connexion
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Modale d'authentification : Google (inchangé) + lien magique par e-mail */}
      {authOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          data-testid="auth-modal"
          onClick={() => { setAuthOpen(false); setMagicSent(false); }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-[#5C6A61] hover:text-[#2A312C]"
              onClick={() => { setAuthOpen(false); setMagicSent(false); }}
              data-testid="auth-modal-close"
              aria-label="Fermer"
            >✕</button>
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-6 text-center">
              Connexion à Mitonner
            </h2>

            {magicSent ? (
              <div className="text-center space-y-3" data-testid="magic-sent-message">
                <Mail className="w-10 h-10 text-[#A5BBA8] mx-auto" />
                <p className="text-[#2A312C]">Consultez votre boîte mail.</p>
                <p className="text-sm text-[#5C6A61]">
                  Nous venons de vous envoyer un lien pour vous connecter à Mitonner. Le lien expire dans 15 minutes.
                </p>
                <button className="btn-outline mt-2 text-sm" onClick={() => { setMagicSent(false); setMagicEmail(''); }}>
                  Utiliser une autre adresse
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <button
                  onClick={login}
                  className="btn-primary w-full"
                  data-testid="auth-google-btn"
                >
                  Continuer avec Google
                </button>

                <div className="flex items-center gap-3 my-2">
                  <span className="h-px flex-1 bg-[#E3DED5]" />
                  <span className="text-xs text-[#5C6A61]">ou</span>
                  <span className="h-px flex-1 bg-[#E3DED5]" />
                </div>

                <form onSubmit={submitMagic} className="space-y-3">
                  <label className="text-sm text-[#2A312C] block">Continuer avec mon adresse e-mail</label>
                  <input
                    type="email"
                    required
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                    placeholder="vous@exemple.fr"
                    className="input-default w-full"
                    data-testid="magic-email-input"
                  />
                  <button
                    type="submit"
                    disabled={magicSending}
                    className="btn-secondary w-full disabled:opacity-50"
                    data-testid="magic-submit-btn"
                  >
                    {magicSending ? 'Envoi...' : 'Envoyer le lien de connexion'}
                  </button>
                  <p className="text-xs text-[#5C6A61] text-center">Aucun mot de passe requis.</p>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8DA690] mb-4 block" data-testid="home-eyebrow">
                {homeContent?.eyebrow || 'Préparation de repas à domicile à Hazebrouck et alentours'}
              </span>
              <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-[#2A312C] mb-4">
                {homeContent?.h1 || 'Vos repas de la semaine préparés à domicile'}
              </h1>
              <p className="font-['Cormorant_Garamond'] text-2xl text-[#C86B51] italic mb-6" data-testid="home-slogan">
                {slogan}
              </p>
              <p className="text-lg text-[#5C6A61] mb-8 max-w-lg">
                {heroSubtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {user ? (
                  <Link 
                    to="/client/reservation"
                    className="btn-secondary inline-flex items-center justify-center gap-2"
                    data-testid="cta-reserve-btn"
                  >
                    Réserver maintenant
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                ) : (
                  <button 
                    onClick={() => setAuthOpen(true)}
                    className="btn-secondary inline-flex items-center justify-center gap-2"
                    data-testid="cta-login-btn"
                  >
                    Commencer
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
                <a href="#comment-ca-marche" className="btn-outline">
                  En savoir plus
                </a>
              </div>
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className="inline-flex items-center gap-2 mt-5 text-[#5C6A61] hover:text-[#2A312C] underline underline-offset-4 transition-colors"
                data-testid="hero-contact-btn"
              >
                <Mail className="w-4 h-4 text-[#A5BBA8]" /> Une question ? Contactez-nous
              </a>
            </div>
            <div className="relative animate-fade-in">
              <img 
                src={heroImage} 
                alt="Chef cuisinant à domicile" 
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8DA690] mb-4 block">
              Nos services
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#2A312C]">
              Pourquoi choisir Mitonner ?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="card-default hover:-translate-y-1 transition-transform duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#A5BBA8]/20 flex items-center justify-center text-[#A5BBA8] mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#2A312C] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#5C6A61]">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="comment-ca-marche" className="py-20 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8DA690] mb-4 block">
                Comment ça marche
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#2A312C] mb-8">
                5 étapes simples pour une semaine de repas
              </h2>
              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div key={index} className="flex gap-4" data-testid={`step-${index}`}>
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C86B51] text-white flex items-center justify-center font-semibold">
                      {step.number}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2A312C] mb-1">{step.title}</h3>
                      <p className="text-[#5C6A61]">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src={vegetablesImage} 
                alt="Légumes frais" 
                className="rounded-2xl shadow-xl w-full object-cover aspect-square"
              />
              <div className="absolute -top-6 -right-3 sm:-right-6 bg-[#A5BBA8] rounded-2xl p-6 text-white">
                <Leaf className="w-8 h-8 mb-2" />
                <p className="font-semibold">Produits frais</p>
                <p className="text-sm opacity-90">De saison</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulas Section */}
      <section id="formules" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8DA690] mb-4 block">
              Nos tarifs
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#2A312C]">
              Une formule pour chaque cocon
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto items-stretch">
            {formulas.map((formula, index) => (
              <div 
                key={index}
                className={`card-default relative h-full flex flex-col ${formula.popular ? 'border-[#C86B51] border-2' : ''}`}
                data-testid={`formula-card-${index}`}
              >
                {formula.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C86B51] text-white text-xs font-semibold px-4 py-1 rounded-full">
                    Populaire
                  </div>
                )}
                <div className="text-center">
                  <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#2A312C] mb-2">
                    {formula.name}
                  </h3>
                  <p className="text-[#5C6A61] mb-4">{formula.persons}</p>
                  <p className="text-4xl font-semibold text-[#2A312C] mb-6">{formula.price}</p>
                  {formula.note_html && (
                    <div
                      className="text-sm text-[#5C6A61] mb-6 text-left leading-relaxed break-words"
                      data-testid={`formula-note-${index}`}
                      dangerouslySetInnerHTML={{ __html: formula.note_html }}
                    />
                  )}
                  <ul className="text-left space-y-3 mb-6">
                    <li className="flex items-center gap-2 text-[#5C6A61]">
                      <Check className="w-5 h-5 text-[#A5BBA8]" />
                      10 repas préparés
                    </li>
                    <li className="flex items-center gap-2 text-[#5C6A61]">
                      <Check className="w-5 h-5 text-[#A5BBA8]" />
                      Liste de courses incluse
                    </li>
                    <li className="flex items-center gap-2 text-[#5C6A61]">
                      <Check className="w-5 h-5 text-[#A5BBA8]" />
                      Cuisinier passionné
                    </li>
                  </ul>
                  <p className="text-sm text-[#5C6A61] italic">
                    Formule attribuée automatiquement selon la taille de votre cocon
                  </p>
                </div>
              </div>
            ))}
          </div>
          {pricing.options && pricing.options.length > 0 && (
            <p className="text-center text-[#5C6A61] mt-8" data-testid="public-options-line">
              Options disponibles : {pricing.options.map((o) => o.name).join(' • ')}
            </p>
          )}
        </div>
      </section>

      {/* Menu du moment (B3) - informatif, lecture seule depuis le menu actif */}
      {currentMenu && currentMenu.menu && (
        <section className="py-20 bg-[#F9F6F0]" data-testid="home-current-menu">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-[#C86B51] font-medium mb-2">Le menu du moment</p>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#2A312C]">
                {currentMenu.menu.name}
              </h2>
              {currentMenu.season?.name && (
                <p className="text-[#5C6A61] mt-3">Saison : {currentMenu.season.name}</p>
              )}
            </div>
            <div className="space-y-10">
              {(currentMenu.menu.categories || [])
                .filter((cat) => (cat.recipes || []).length > 0)
                .map((cat) => (
                  <div key={cat.category_id} data-testid={`home-menu-category-${cat.category_id}`}>
                    <h3 className="text-lg font-semibold text-[#2A312C] mb-4">{cat.name}</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {cat.recipes.map((recipe) => (
                        <div key={recipe.recipe_id} className="bg-white rounded-2xl border border-[#E3DED5] p-5">
                          <h4 className="font-medium text-[#2A312C]">{recipe.name}</h4>
                          {recipe.description && (
                            <p className="text-sm text-[#5C6A61] mt-1 line-clamp-3">{recipe.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
            <p className="text-center text-sm text-[#5C6A61] mt-10">
              Aperçu du menu de saison. La composition de vos repas se fait lors de la réservation.
            </p>
          </div>
        </section>
      )}

      {/* Avis mis en avant (B2) - rien si aucun avis sélectionné */}
      {featuredReview && (
        <section className="py-20 bg-white" data-testid="home-featured-review">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-1 mb-5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-6 h-6 ${n <= featuredReview.rating ? 'text-[#E0A458] fill-[#E0A458]' : 'text-[#D8D2C7]'}`}
                />
              ))}
            </div>
            <blockquote className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl text-[#2A312C] leading-relaxed">
              « {featuredReview.comment} »
            </blockquote>
            <p className="text-[#5C6A61] mt-6 font-medium">— {featuredReview.display_name}</p>
            <Link to="/avis" className="inline-flex items-center gap-2 mt-6 text-[#C86B51] font-medium hover:underline" data-testid="home-featured-review-link">
              Voir tous les avis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-[#2A312C]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#F9F6F0] mb-6">
            {ctaTitle}
          </h2>
          <div className="text-[#A5BBA8] text-lg mb-8 max-w-2xl mx-auto break-words" dangerouslySetInnerHTML={{ __html: ctaSubtitle }} />
          {user ? (
            <Link 
              to="/client/reservation"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="cta-bottom-btn"
            >
              Réserver ma première session
              <ArrowRight className="w-5 h-5" />
            </Link>
          ) : (
            <button 
              onClick={login}
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="cta-bottom-login-btn"
            >
              Créer mon compte
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="a-propos" className="py-20 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src={aboutImage} 
                alt="Famille à table" 
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8DA690] mb-4 block">
                Notre mission
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#2A312C] mb-6">
                {aboutTitle}
              </h2>
              <div className="text-[#5C6A61] mb-6 break-words" dangerouslySetInnerHTML={{ __html: aboutText1 }} />
              <div className="text-[#5C6A61] mb-6 break-words" dangerouslySetInnerHTML={{ __html: aboutText2 }} />
              <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-[#E3DED5]">
                <div className="w-12 h-12 rounded-full bg-[#A5BBA8]/20 flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#A5BBA8]" />
                </div>
                <div>
                  <p className="font-semibold text-[#2A312C]">Éligible au crédit d&apos;impôt</p>
                  <p className="text-sm text-[#5C6A61]">Service à la personne - Avance immédiate URSSAF possible</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 bg-white scroll-mt-24" data-testid="contact-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl sm:text-5xl font-serif text-[#2A312C] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Contact</h2>
            <p className="text-[#5C6A61]">Une question ? Écrivez-nous, nous vous répondrons dans les meilleurs délais.</p>
          </div>

          {contactStatus.success ? (
            <div className="p-6 rounded-2xl bg-[#A5BBA8]/15 border border-[#A5BBA8]/30 text-center" data-testid="contact-success">
              <p className="text-[#2A312C] font-medium">Merci ! Votre demande a bien été envoyée. Nous vous répondrons dans les meilleurs délais.</p>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="bg-[#F9F6F0] p-6 sm:p-8 rounded-2xl border border-[#E3DED5] space-y-5" data-testid="contact-form">
              {/* Honeypot anti-spam (masqué aux visiteurs) */}
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={contactForm.website}
                onChange={(e) => setContactForm({ ...contactForm, website: e.target.value })}
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                aria-hidden="true"
              />

              <div>
                <label className="block text-sm text-[#2A312C] mb-1">Nom et prénom <span className="text-[#C86B51]">*</span></label>
                <input
                  type="text" required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#E3DED5] bg-white focus:outline-none focus:border-[#A5BBA8]"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#2A312C] mb-1">Adresse e-mail <span className="text-[#C86B51]">*</span></label>
                <input
                  type="email" required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#E3DED5] bg-white focus:outline-none focus:border-[#A5BBA8]"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-sm text-[#2A312C] mb-1">Numéro de téléphone {contactForm.callback && <span className="text-[#C86B51]">*</span>}</label>
                <input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#E3DED5] bg-white focus:outline-none focus:border-[#A5BBA8]"
                  data-testid="contact-phone-input"
                />
                <p className="text-xs text-[#5C6A61] mt-1">Votre numéro sera uniquement utilisé pour répondre à votre demande.</p>
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="contact-callback"
                  type="checkbox"
                  checked={contactForm.callback}
                  onChange={(e) => setContactForm({ ...contactForm, callback: e.target.checked })}
                  className="mt-1"
                  data-testid="contact-callback-checkbox"
                />
                <label htmlFor="contact-callback" className="text-sm text-[#2A312C]">Je souhaite être rappelé(e)</label>
              </div>

              <div>
                <label className="block text-sm text-[#2A312C] mb-1">Message <span className="text-[#C86B51]">*</span></label>
                <textarea
                  required rows={5}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[#E3DED5] bg-white focus:outline-none focus:border-[#A5BBA8] resize-y"
                  data-testid="contact-message-input"
                />
              </div>

              {contactStatus.error && (
                <p className="text-sm text-[#C86B51]" data-testid="contact-error">{contactStatus.error}</p>
              )}

              <button
                type="submit"
                disabled={contactStatus.loading}
                className="btn-primary w-full sm:w-auto disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {contactStatus.loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Maillage interne SEO — liens descriptifs crawlables */}
      <section className="bg-white border-t border-[#E3DED5]" data-testid="home-internal-links">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#2A312C] mb-6">Explorer Mitonner</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-[#C86B51] font-medium">
            <Link to="/comment-ca-marche" className="hover:underline">Découvrir comment fonctionne Mitonner</Link>
            <Link to="/menus" className="hover:underline">Voir les menus de saison</Link>
            <Link to="/tarifs" className="hover:underline">Voir les tarifs de préparation de repas à domicile</Link>
            <Link to="/services-a-la-personne" className="hover:underline">Découvrir le crédit d&apos;impôt de 50 %</Link>
            <Link to="/zone-intervention" className="hover:underline">Consulter notre zone d&apos;intervention</Link>
            <Link to="/contenants-conservation" className="hover:underline">Conseils de conservation des repas</Link>
            <Link to="/avis" className="hover:underline">Lire les avis clients</Link>
            <Link to="/a-propos" className="hover:underline">En savoir plus à propos de Mitonner</Link>
            <Link to="/contact" className="hover:underline">Contacter Mitonner</Link>
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="bg-[#2A312C] text-[#F9F6F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* SAP Banner */}
          {sapEnabled && (
            <div className="mb-8 p-4 bg-[#A5BBA8]/10 rounded-xl border border-[#A5BBA8]/20" data-testid="sap-footer-banner">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-center sm:text-left">
                  <Percent className="w-8 h-8 text-[#A5BBA8] hidden sm:block" />
                  <div>
                    <p className="font-semibold text-white">{sapMarketingText}</p>
                    <p className="text-sm text-[#A5BBA8]">
                      {sapDeclarationText} <strong>{sapNumber}</strong>
                    </p>
                  </div>
                </div>
                <Link 
                  to="/services-a-la-personne" 
                  className="px-4 py-2 bg-[#A5BBA8] text-[#2A312C] rounded-lg hover:bg-[#8DA890] transition-colors text-sm font-medium whitespace-nowrap"
                  data-testid="sap-footer-link"
                >
                  En savoir plus
                </Link>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <img 
                src={config?.logo_white_url || logoUrl} 
                alt={config?.company_name || 'Mitonner'} 
                className="h-12 mb-4 brightness-0 invert object-contain" 
              />
              <p className="text-[#A5BBA8]">
                Cuisine à domicile pour des repas faits maison toute la semaine.
              </p>
              {/* Social Media Icons */}
              <div className="flex items-center gap-3 mt-4">
                {socialMedia.facebook && (
                  <a href={socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-[#A5BBA8] hover:text-white transition-colors" aria-label="Facebook">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {socialMedia.instagram && (
                  <a href={socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-[#A5BBA8] hover:text-white transition-colors" aria-label="Instagram">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {socialMedia.linkedin && (
                  <a href={socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#A5BBA8] hover:text-white transition-colors" aria-label="LinkedIn">
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {socialMedia.youtube && (
                  <a href={socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="text-[#A5BBA8] hover:text-white transition-colors" aria-label="YouTube">
                    <Youtube className="w-5 h-5" />
                  </a>
                )}
                {socialMedia.twitter && (
                  <a href={socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-[#A5BBA8] hover:text-white transition-colors" aria-label="Twitter">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Navigation</h4>
              <ul className="space-y-2">
                <li><Link to="/comment-ca-marche" className="text-[#A5BBA8] hover:text-white transition-colors">Comment ça marche</Link></li>
                <li><Link to="/menus" className="text-[#A5BBA8] hover:text-white transition-colors">Nos menus</Link></li>
                <li><Link to="/tarifs" className="text-[#A5BBA8] hover:text-white transition-colors">Tarifs</Link></li>
                <li><Link to="/zone-intervention" className="text-[#A5BBA8] hover:text-white transition-colors">Zone d&apos;intervention</Link></li>
                <li><Link to="/a-propos" className="text-[#A5BBA8] hover:text-white transition-colors">À propos</Link></li>
                <li><Link to="/contact" className="text-[#A5BBA8] hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/avis" className="text-[#A5BBA8] hover:text-white transition-colors">Avis clients</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Informations</h4>
              <ul className="space-y-2">
                {sapEnabled && (
                  <li><Link to="/services-a-la-personne" className="text-[#A5BBA8] hover:text-white transition-colors">Services à la personne</Link></li>
                )}
                <li><Link to="/contenants-conservation" className="text-[#A5BBA8] hover:text-white transition-colors">Contenants & Conservation</Link></li>
                <li><Link to="/cgv" className="text-[#A5BBA8] hover:text-white transition-colors">CGV</Link></li>
                <li><Link to="/mentions-legales" className="text-[#A5BBA8] hover:text-white transition-colors">Mentions légales</Link></li>
                <li><Link to="/confidentialite" className="text-[#A5BBA8] hover:text-white transition-colors">Politique de confidentialité</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-[#A5BBA8]">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${contactEmail}`} className="hover:text-white transition-colors">{contactEmail}</a>
                </li>
                <li className="flex items-center gap-2 text-[#A5BBA8]">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${contactPhone.replace(/\s/g, '')}`} onClick={() => trackPhoneClick('footer')} className="hover:text-white transition-colors" data-testid="footer-phone-link">{contactPhone}</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#5C6A61]/30 pt-8 text-center text-[#A5BBA8]">
            <p>{footerText}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
